/**
 * 🔒 A site photo's handling on upload (piece 9, *Image types and compression*; decisions 6380, 6381, 6382; row
 * T2916), from the owner's measured test of 2026-10-20 on the HarbisonWalker kiln upgrade's fourteen photos. An
 * image type's standard is a long edge and a JPEG quality, 2400 px and 75 for the Site Photo type, and no setting
 * goes under the 2000 px floor. The checks read the original while the person is still on site: a low camera
 * resolution, a blurred picture, an earlier copy on the same record and text reading as a credential. A site photo on
 * a Deal names the machines or units it shows. 🔨 The rules here are pure; the server's encoder and the screens read
 * them, so the phone, the web and the server never disagree.
 */

/** 🔒 The long edge no image type's standard goes under, the owner's test: small print in a wide shot goes soft below it. */
export const IMAGE_LONG_EDGE_FLOOR = 2000;

/** The most pixels a long edge is set to; a camera writes 8000 at the extreme. */
export const IMAGE_LONG_EDGE_MAX = 12000;

/** 🔒 The Site Photo type's standard, the owner's measured level: every catalog number still readable at about half a megabyte. */
export const SITE_PHOTO_LONG_EDGE = 2400;
export const SITE_PHOTO_JPEG_QUALITY = 75;

/** The Site Photo type's name, which the seed and the migration give the standard to. */
export const SITE_PHOTO_FILE_TYPE_NAME = 'Site Photo';

/** What a type carries for its standard; the type list answers these columns. */
export interface ImageStandardFacts {
  compressOnUpload?: boolean | null;
  longEdgePx?: number | null;
  jpegQuality?: number | null;
}

export interface ImageStandard {
  longEdgePx: number;
  jpegQuality: number;
}

/**
 * 🔒 A type's image standard (decision 6380): in force where the type compresses on upload and names a long edge,
 * the quality the type names or 75. Null where it names none, and decision 4621's DPI reading stands for the type.
 */
export function imageStandardOf(type: ImageStandardFacts | null | undefined): ImageStandard | null {
  if (!type || type.compressOnUpload !== true) return null;
  const longEdgePx = type.longEdgePx ?? null;
  if (longEdgePx === null || !Number.isInteger(longEdgePx) || longEdgePx < IMAGE_LONG_EDGE_FLOOR) return null;
  const quality = type.jpegQuality ?? null;
  return { longEdgePx, jpegQuality: quality !== null && Number.isInteger(quality) && quality >= 1 && quality <= 100 ? quality : SITE_PHOTO_JPEG_QUALITY };
}

/** 🔒 What the Settings write refuses in a long edge, or null where it is taken; blank clears the standard. The floor refuses, it never clamps. */
export function longEdgeProblem(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value !== 'number' || !Number.isInteger(value)) return 'A long edge is a whole number of pixels.';
  if (value < IMAGE_LONG_EDGE_FLOOR) return `A long edge is at least ${IMAGE_LONG_EDGE_FLOOR} px, where the small print in a wide shot still reads; ${value} is under it.`;
  if (value > IMAGE_LONG_EDGE_MAX) return `A long edge is at most ${IMAGE_LONG_EDGE_MAX} px; ${value} is over it.`;
  return null;
}

/** What the Settings write refuses in a JPEG quality, or null where it is taken; blank stands for 75. */
export function jpegQualityProblem(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 1 || value > 100) return 'A JPEG quality is a whole number from 1 to 100.';
  return null;
}

/** 🔒 The Settings row holding the blur check's threshold, the variance of the Laplacian a sharp picture reaches (decision 6380). */
export const PHOTO_BLUR_THRESHOLD_KEY = 'files.photoBlurThreshold';

/** 🔒 The threshold's starting value, the one the check is tuned from on real uploads; the owner's document says tune it. */
export const PHOTO_BLUR_THRESHOLD_DEFAULT = 60;

/** The long edge the blur check measures a picture at, so one threshold serves a 12 MP camera and a 48 MP one alike. */
export const BLUR_MEASURE_LONG_EDGE = 1000;

/** What the Settings write refuses in the blur threshold, or null where it is taken; blank stands for the starting value. */
export function blurThresholdProblem(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > 100000) return 'The blur threshold is a number from 0 to 100000.';
  return null;
}

/** The threshold in force: the row's number, or the starting value where none is held. */
export function blurThresholdOf(held: unknown): number {
  return typeof held === 'number' && Number.isFinite(held) && held >= 0 ? held : PHOTO_BLUR_THRESHOLD_DEFAULT;
}

/**
 * 🔒 The variance of the Laplacian over a greyscale picture, one byte a pixel, row by row (decision 6380): the four
 * neighbours less four times the pixel, over every pixel off the edge. A sharp picture's edges answer high and a
 * blurred one's answer low.
 */
export function laplacianVariance(grey: Uint8Array, width: number, height: number): number {
  if (width < 3 || height < 3 || grey.length < width * height) return 0;
  let sum = 0;
  let squares = 0;
  let count = 0;
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const at = y * width + x;
      const value = grey[at - 1]! + grey[at + 1]! + grey[at - width]! + grey[at + width]! - 4 * grey[at]!;
      sum += value;
      squares += value * value;
      count += 1;
    }
  }
  const mean = sum / count;
  return squares / count - mean * mean;
}

/**
 * 🔒 The perceptual hash's grid (decision 6380): a 32 × 32 greyscale picture whose cosine transform's lowest 16 × 16
 * terms are read as 256 bits, each a term above the median of the 255 after the first. A low term reads the scene's
 * layout, so a re-encode, a resize or a little more light moves few bits, and a flat wall's grain moves none.
 */
export const PHASH_SIZE = 32;
export const PHASH_BLOCK = 16;

/** 🔒 Two pictures whose hashes differ in this many bits or fewer are near duplicates, about one bit in sixteen (decision 6380). */
export const NEAR_DUPLICATE_BITS = 16;

/** The perceptual hash of a 32 × 32 greyscale grid as 64 hex digits; the server's encoder draws the grid. */
export function perceptualHash(grid: Uint8Array): string {
  const size = PHASH_SIZE;
  if (grid.length < size * size) throw new Error(`A perceptual hash reads a ${size} × ${size} grid.`);
  const cosines: number[][] = [];
  for (let k = 0; k < PHASH_BLOCK; k += 1) {
    cosines.push(Array.from({ length: size }, (_, n) => Math.cos(((2 * n + 1) * k * Math.PI) / (2 * size))));
  }
  // The rows' transform first, then the columns', kept to the low block the hash reads.
  const rows = new Float64Array(size * PHASH_BLOCK);
  for (let y = 0; y < size; y += 1) {
    for (let u = 0; u < PHASH_BLOCK; u += 1) {
      let sum = 0;
      for (let x = 0; x < size; x += 1) sum += grid[y * size + x]! * cosines[u]![x]!;
      rows[y * PHASH_BLOCK + u] = sum;
    }
  }
  const terms: number[] = [];
  for (let v = 0; v < PHASH_BLOCK; v += 1) {
    for (let u = 0; u < PHASH_BLOCK; u += 1) {
      let sum = 0;
      for (let y = 0; y < size; y += 1) sum += rows[y * PHASH_BLOCK + u]! * cosines[v]![y]!;
      terms.push(sum);
    }
  }
  const sorted = terms.slice(1).sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)]!;
  let hex = '';
  for (let at = 0; at < terms.length; at += 4) {
    let nibble = 0;
    for (let bit = 0; bit < 4; bit += 1) nibble = (nibble << 1) | (terms[at + bit]! > median ? 1 : 0);
    hex += nibble.toString(16);
  }
  return hex;
}

/** How many bits two hashes of one length differ in; a hash of another length is as far as can be. */
export function hashDistance(a: string, b: string): number {
  if (a.length !== b.length) return Number.POSITIVE_INFINITY;
  let distance = 0;
  for (let index = 0; index < a.length; index += 1) {
    let x = parseInt(a[index]!, 16) ^ parseInt(b[index]!, 16);
    while (x) {
      distance += x & 1;
      x >>= 1;
    }
  }
  return distance;
}

/** Whether two pictures' hashes name one scene. */
export function isNearDuplicate(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  return hashDistance(a, b) <= NEAR_DUPLICATE_BITS;
}

/**
 * 🔒 Whether text read off a picture reads as a password, a PIN or a credential (decision 6380). A word that only
 * ever names a secret flags alone; a word that also names a part, such as a connector's PIN 3 or a login screen's
 * title, flags only with a colon, an equals sign or a hash after it, as a written-down secret carries.
 */
export function credentialText(text: string | null | undefined): boolean {
  if (!text) return false;
  const alone = /\b(passwords?|passwd|passcodes?|pass\s?code|credentials?|wpa2?\s?key|wi-?fi\s?(?:key|password)|network\s?key|security\s?key)\b/i;
  const labelled = /\b(pwd|pin(?:\s?(?:code|number|no\.?))?|login|log-?in|user\s?(?:name|id)|username|access\s?code|admin|secret)\s*[:=#]/i;
  return alone.test(text) || labelled.test(text);
}

/** 🔒 The most units one photo names, and the longest name a unit takes (decision 6382). */
export const UNIT_TAGS_MAX = 10;
export const UNIT_TAG_MAX_LENGTH = 60;

/** The multipart field an upload carries its units in: a JSON list of names, or one name. */
export const UNIT_TAGS_FIELD = 'unitTags';

/** The read of the units already named on a record's photos, which a photo's unit field offers (decision 6382). */
export const FILE_UNITS_PATH = '/files/units';

/** One unit's name as stored: trimmed, inner runs of space folded to one; empty is none. */
export function cleanUnitTag(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const clean = value.trim().replace(/\s+/g, ' ');
  return clean === '' ? null : clean;
}

/**
 * 🔒 The units an upload names (decision 6382): a list, a JSON list in one field, or one name; each cleaned, once
 * each without regard to case, in the order given. A comma inside a typed field separates two units.
 */
export function unitTagsOf(value: unknown): string[] {
  let parts: unknown[];
  if (Array.isArray(value)) parts = value;
  else if (typeof value === 'string' && value.trim().startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(value);
      parts = Array.isArray(parsed) ? parsed : [];
    } catch {
      // silent: a field that is not a JSON list is one unit's name, read as typed
      parts = [value];
    }
  } else parts = [value];
  const units: string[] = [];
  for (const part of parts.flatMap((one) => (typeof one === 'string' ? one.split(',') : [one]))) {
    const clean = cleanUnitTag(part);
    if (clean !== null && !units.some((unit) => unit.toLowerCase() === clean.toLowerCase())) units.push(clean);
  }
  return units;
}

/** What the upload refuses in the units it names, or null where they are taken. */
export function unitTagsProblem(units: readonly string[]): string | null {
  if (units.length > UNIT_TAGS_MAX) return `A photo names at most ${UNIT_TAGS_MAX} units; this one names ${units.length}.`;
  const long = units.find((unit) => unit.length > UNIT_TAG_MAX_LENGTH);
  if (long !== undefined) return `A unit's name is at most ${UNIT_TAG_MAX_LENGTH} characters; one is ${long.length}.`;
  return null;
}

/** 🔒 The refusal of a site photo on a Deal sent with no unit (decision 6382), the owner's Rule 4: blocked until it is tagged. */
export const UNIT_TAG_REQUIRED = 'A site photo on a Deal names the machine or unit it shows, such as Preheat #2. Tag it with one or more units, then send it again.';

/** Whether an upload is a site photo on a Deal that must name its units: a picture of a type carrying the standard, tagged to a Deal. */
export function unitTagRequired(input: { taggedRecordType?: string | null; mimeType: string; standard: ImageStandard | null }): boolean {
  return input.taggedRecordType === 'Deal' && input.standard !== null && input.mimeType.toLowerCase().startsWith('image/');
}

/**
 * 🔒 The labels a filed photo is named from with its units inside (decision 6382): every unit before the item, the
 * last label, so a spot's photo reads `<section>-<subsection>-<unit>-<item>-<n>` as the owner's Rule 4 asks.
 */
export function filedLabels(labels: readonly string[], units: readonly string[]): string[] {
  if (units.length === 0 || labels.length === 0) return [...labels, ...units];
  return [...labels.slice(0, -1), ...units, labels[labels.length - 1]!];
}

/** 🔒 The four checks an upload answers while the person is still on site (decision 6380). */
export const PHOTO_CHECK_KINDS = ['lowResolution', 'blurred', 'duplicate', 'credential'] as const;
export type PhotoCheckKind = (typeof PHOTO_CHECK_KINDS)[number];

export interface PhotoCheck {
  kind: PhotoCheckKind;
  /** On a duplicate: the earlier file on the record, and whether its bytes are the same or only its picture. */
  duplicateOf?: { id: string; filename: string; exact: boolean };
}

/** The checks' answer on an upload: the original's long edge, the stored copy's, and what failed. */
export interface PhotoChecks {
  longEdgePx: number;
  storedLongEdgePx: number;
  /** The type's standard, which the low-resolution line names. */
  standardLongEdgePx: number;
  checks: PhotoCheck[];
}

/** The string key each check's line reads under. */
export const PHOTO_CHECK_LINE_KEYS: Readonly<Record<PhotoCheckKind, string>> = {
  lowResolution: 'photo.check.lowResolution',
  blurred: 'photo.check.blurred',
  duplicate: 'photo.check.duplicate',
  credential: 'photo.check.credential',
};

/** 🔒 The five coaching prompts the upload screen carries on the phone and the web, in the owner's words (decision 6380). */
export const PHOTO_COACHING_PROMPT_KEYS = ['photo.coach.fillFrame', 'photo.coach.wideAndClose', 'photo.coach.oneLabel', 'photo.coach.glare', 'photo.coach.check'] as const;

/** 🔒 The feed entry a credential seen on a photo writes for the uploader and the Deal's engineers; it names the file, never the text. */
export const CREDENTIAL_SEEN_FEED_ENTRY = 'file.credentialSeen';
